import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, apiFetch, API } from '../../context/AuthContext';
import './Dashboard.css';

// ── Toast ──────────────────────────────────────────────
function Toast({ msg, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2400); return () => clearTimeout(t); }, [onDone]);
  return <div className="toast">{msg}</div>;
}

// ── Modal ──────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Input helpers ───────────────────────────────────────
const Field = ({ label, hint, children }) => (
  <div className="modal-field">
    <label>{label}</label>
    {children}
    {hint && <span className="field-hint">{hint}</span>}
  </div>
);

// ══════════════════════════════════════════════════════
// TAB: OVERVIEW
// ══════════════════════════════════════════════════════
function OverviewTab({ counts }) {
  const cards = [
    { num: counts.portfolio,    label: 'Portfolio Items' },
    { num: counts.services,     label: 'Services' },
    { num: counts.team,         label: 'Team Members' },
    { num: counts.stats,        label: 'Stat Counters' },
  ];
  return (
    <>
      <div className="overview-grid">
        {cards.map(c => (
          <div className="ov-card" key={c.label}>
            <div className="ov-num">{c.num ?? '—'}</div>
            <div className="ov-label">{c.label}</div>
          </div>
        ))}
      </div>
      <p style={{ color: 'var(--muted)', fontSize: '0.82rem', lineHeight: 1.7 }}>
        Use the sidebar to manage portfolio items, services, your team, brands,
        stat counters, and global site settings. Changes are reflected on the
        public website immediately.
      </p>
    </>
  );
}

// ══════════════════════════════════════════════════════
// TAB: PORTFOLIO
// ══════════════════════════════════════════════════════
// ── Service / sub-service filing (shared by Portfolio & Videos) ──
function useServices() {
  const [services, setServices] = useState([]);
  useEffect(() => {
    apiFetch('/api/content/services/all').then(d => { if (d) setServices(d); });
  }, []);
  return services;
}

// "Photography › Wedding & Engagement" for a photo/video row
function serviceLabel(services, item) {
  const svc = services.find(s => s.id === item.service_id);
  if (!svc) return null;
  const sub = svc.sub_services?.find(ss => ss.id === item.sub_service_id);
  return sub ? `${svc.name} › ${sub.name}` : svc.name;
}

function ServicePicker({ services, form, set }) {
  const svc = services.find(s => s.id === Number(form.service_id));
  return (
    <div className="modal-row">
      <Field label="Service">
        <select
          value={form.service_id || ''}
          onChange={e => { set('service_id', e.target.value ? Number(e.target.value) : null); set('sub_service_id', null); }}
        >
          <option value="">— Not filed —</option>
          {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </Field>
      <Field label="Sub-service">
        <select
          value={form.sub_service_id || ''}
          disabled={!svc || !svc.sub_services?.length}
          onChange={e => set('sub_service_id', e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">{svc ? `— All ${svc.name} —` : '— Pick a service first —'}</option>
          {svc?.sub_services?.map(ss => <option key={ss.id} value={ss.id}>{ss.name}</option>)}
        </select>
      </Field>
    </div>
  );
}

const EMPTY_PORT = { title: '', image_url: '', tall: false, display_order: 0, service_id: null, sub_service_id: null };

function PortfolioTab(props) {
  const { toast } = props;
  const [items, setItems]     = useState([]);
  const [loading, setLd]      = useState(true);
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState(EMPTY_PORT);
  const [imgMode, setImgMode] = useState('url'); // 'url' | 'upload'
  const [uploading, setUpl]   = useState(false);
  const fileRef               = useRef();
  const services              = useServices();

  const load = useCallback(async () => {
    setLd(true);
    const data = await apiFetch('/api/portfolio');
    if (data) setItems(data);
    setLd(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd  = useCallback(() => { setForm(EMPTY_PORT); setImgMode('url'); setModal('add'); }, []);
  const openEdit = (item) => { setForm({ ...item, tall: !!item.tall }); setImgMode('url'); setModal(item); };

  useEffect(() => { if (props.addTrigger > 0) openAdd(); }, [props.addTrigger, openAdd]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUpl(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set('image_url', res.data.url);
      toast('Image uploaded');
    } catch {
      toast('Upload failed');
    } finally {
      setUpl(false);
    }
  };

  const save = async () => {
    if (modal === 'add') {
      await apiFetch('/api/portfolio', { method: 'POST', body: JSON.stringify(form) });
    } else {
      await apiFetch(`/api/portfolio/${modal.id}`, { method: 'PUT', body: JSON.stringify(form) });
    }
    setModal(null);
    toast('Saved');
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    await apiFetch(`/api/portfolio/${id}`, { method: 'DELETE' });
    toast('Deleted');
    load();
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <>
      <div className="dash-table-wrap">
        {loading ? <div className="dash-loading">Loading…</div> : (
          <table className="dash-table">
            <thead>
              <tr>
                <th>Image</th><th>Title</th><th>Service</th><th>Tall</th><th>Order</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id}>
                  <td><img className="td-thumb" src={item.image_url} alt={item.title} /></td>
                  <td className="td-title">{item.title}</td>
                  <td>
                    {serviceLabel(services, item)
                      ? <span className="td-badge">{serviceLabel(services, item)}</span>
                      : <span className="td-badge inactive">Not filed</span>}
                  </td>
                  <td>{item.tall ? '✓' : '—'}</td>
                  <td>{item.display_order}</td>
                  <td>
                    <div className="td-actions">
                      <button className="btn-icon" onClick={() => openEdit(item)}>Edit</button>
                      <button className="btn-icon danger" onClick={() => del(item.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && items.length === 0 && <div className="dash-empty">No portfolio items yet.</div>}
      </div>

      {modal && (
        <Modal title={modal === 'add' ? 'Add Portfolio Item' : 'Edit Item'} onClose={() => setModal(null)}>
          <div className="modal-body">
            <Field label="Title">
              <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Eternal Vows" />
            </Field>
            <ServicePicker services={services} form={form} set={set} />
            <div className="modal-row">
              <Field label="Display Order">
                <input type="number" value={form.display_order} onChange={e => set('display_order', +e.target.value)} />
              </Field>
            </div>
            <div className="modal-field">
              <label>Image</label>
              <div style={{ display:'flex', gap:'0.5rem', marginBottom:'0.6rem' }}>
                <button type="button" className={`filter-btn${imgMode==='url'?' active':''}`} onClick={() => setImgMode('url')}>Paste URL</button>
                <button type="button" className={`filter-btn${imgMode==='upload'?' active':''}`} onClick={() => setImgMode('upload')}>Upload File</button>
              </div>
              {imgMode === 'url' ? (
                <input value={form.image_url} onChange={e => set('image_url', e.target.value)} placeholder="https://…" />
              ) : (
                <div style={{ display:'flex', alignItems:'center', gap:'0.8rem' }}>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} style={{ color:'var(--muted)', fontSize:'0.8rem', flex:1 }} />
                  {uploading && <span style={{ fontSize:'0.72rem', color:'var(--muted)' }}>Uploading…</span>}
                </div>
              )}
            </div>
            {form.image_url && <img className="modal-preview" src={form.image_url} alt="preview" />}
            <div className="modal-check-row">
              <input
                type="checkbox"
                id="tall-check"
                checked={!!form.tall}
                onChange={e => set('tall', e.target.checked)}
              />
              <label htmlFor="tall-check">Tall item (spans 2 rows in the grid)</label>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn-save" onClick={save}>Save</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════
// TAB: VIDEOS
// ══════════════════════════════════════════════════════
const EMPTY_VID = { title: '', description: '', youtube_url: '', thumbnail_url: '', display_order: 0, active: true, service_id: null, sub_service_id: null };

function getYtId(url = '') {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:.*[?&]v=|.*\/))([^?&/\s]{11})/);
  return m ? m[1] : null;
}

function VideosTab(props) {
  const { toast } = props;
  const [list, setList]     = useState([]);
  const [loading, setLd]    = useState(true);
  const [modal, setModal]   = useState(null);
  const [form, setForm]     = useState(EMPTY_VID);
  const [thumbMode, setThumbMode] = useState('url');
  const [uploading, setUpl] = useState(false);
  const fileRef             = useRef();
  const services            = useServices();

  const load = useCallback(async () => {
    setLd(true);
    const data = await apiFetch('/api/videos/all');
    if (data) setList(data);
    setLd(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd  = useCallback(() => { setForm(EMPTY_VID); setThumbMode('url'); setModal('add'); }, []);
  const openEdit = (v) => { setForm({ ...v, active: !!v.active }); setThumbMode('url'); setModal(v); };

  useEffect(() => { if (props.addTrigger > 0) openAdd(); }, [props.addTrigger, openAdd]);

  const save = async () => {
    if (modal === 'add') {
      await apiFetch('/api/videos', { method: 'POST', body: JSON.stringify(form) });
    } else {
      await apiFetch(`/api/videos/${modal.id}`, { method: 'PUT', body: JSON.stringify(form) });
    }
    setModal(null);
    toast('Saved');
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    await apiFetch(`/api/videos/${id}`, { method: 'DELETE' });
    toast('Deleted');
    load();
  };

  const handleThumbUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUpl(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, thumbnail_url: res.data.url }));
      toast('Thumbnail uploaded');
    } catch {
      toast('Upload failed');
    } finally {
      setUpl(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <>
      <div className="dash-table-wrap">
        {loading ? <div className="dash-loading">Loading…</div> : (
          <table className="dash-table">
            <thead>
              <tr><th>Preview</th><th>Title</th><th>Service</th><th>YouTube</th><th>Order</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {list.map(v => {
                const ytId = getYtId(v.youtube_url);
                return (
                  <tr key={v.id}>
                    <td>
                      {ytId
                        ? <img className="td-thumb" src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt={v.title} />
                        : v.thumbnail_url
                          ? <img className="td-thumb" src={v.thumbnail_url} alt={v.title} />
                          : <span style={{ color:'var(--muted)', fontSize:'1.2rem' }}>▶</span>
                      }
                    </td>
                    <td className="td-title">{v.title}</td>
                    <td>
                      {serviceLabel(services, v)
                        ? <span className="td-badge">{serviceLabel(services, v)}</span>
                        : <span className="td-badge inactive">Not filed</span>}
                    </td>
                    <td style={{ color:'var(--muted)', fontSize:'0.75rem', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {v.youtube_url || '—'}
                    </td>
                    <td>{v.display_order}</td>
                    <td><span className={`td-badge ${v.active ? 'active' : 'inactive'}`}>{v.active ? 'Active' : 'Hidden'}</span></td>
                    <td>
                      <div className="td-actions">
                        <button className="btn-icon" onClick={() => openEdit(v)}>Edit</button>
                        <button className="btn-icon danger" onClick={() => del(v.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && list.length === 0 && <div className="dash-empty">No videos yet. Add a YouTube link or upload a thumbnail.</div>}
      </div>

      {modal && (
        <Modal title={modal === 'add' ? 'Add Video' : 'Edit Video'} onClose={() => setModal(null)}>
          <div className="modal-body">
            <Field label="Title">
              <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Behind the Shoot" />
            </Field>
            <Field label="Description (optional)">
              <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)} placeholder="Short description…" />
            </Field>
            <ServicePicker services={services} form={form} set={set} />
            <Field label="YouTube URL" hint="Leave blank if using a thumbnail image instead">
              <input value={form.youtube_url} onChange={e => set('youtube_url', e.target.value)} placeholder="https://youtube.com/watch?v=…" />
            </Field>
            {getYtId(form.youtube_url) && (
              <img
                className="modal-preview"
                src={`https://img.youtube.com/vi/${getYtId(form.youtube_url)}/mqdefault.jpg`}
                alt="YouTube thumbnail"
              />
            )}
            {!getYtId(form.youtube_url) && (
              <div className="modal-field">
                <label>Thumbnail Image (if no YouTube link)</label>
                <div style={{ display:'flex', gap:'0.5rem', marginBottom:'0.6rem' }}>
                  <button type="button" className={`filter-btn${thumbMode==='url'?' active':''}`} onClick={() => setThumbMode('url')}>Paste URL</button>
                  <button type="button" className={`filter-btn${thumbMode==='upload'?' active':''}`} onClick={() => setThumbMode('upload')}>Upload File</button>
                </div>
                {thumbMode === 'url' ? (
                  <input value={form.thumbnail_url} onChange={e => set('thumbnail_url', e.target.value)} placeholder="https://…" />
                ) : (
                  <div style={{ display:'flex', alignItems:'center', gap:'0.8rem' }}>
                    <input ref={fileRef} type="file" accept="image/*" onChange={handleThumbUpload} style={{ color:'var(--muted)', fontSize:'0.8rem', flex:1 }} />
                    {uploading && <span style={{ fontSize:'0.72rem', color:'var(--muted)' }}>Uploading…</span>}
                  </div>
                )}
                {form.thumbnail_url && <img className="modal-preview" src={form.thumbnail_url} alt="preview" style={{ marginTop:'0.5rem' }} />}
              </div>
            )}
            <div className="modal-row">
              <Field label="Display Order">
                <input type="number" value={form.display_order} onChange={e => set('display_order', +e.target.value)} />
              </Field>
            </div>
            <div className="modal-check-row">
              <input type="checkbox" id="vid-active" checked={!!form.active} onChange={e => set('active', e.target.checked)} />
              <label htmlFor="vid-active">Active (visible on website)</label>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn-save" onClick={save}>Save</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════
// TAB: TEAM
// ══════════════════════════════════════════════════════
const EMPTY_MEMBER = { name: '', role: '', photo_url: '', sort_order: 0, active: true };

function TeamTab(props) {
  const { toast } = props;
  const [list, setList]       = useState([]);
  const [loading, setLd]      = useState(true);
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState(EMPTY_MEMBER);
  const [imgMode, setImgMode] = useState('url');
  const [uploading, setUpl]   = useState(false);
  const fileRef               = useRef();

  const load = useCallback(async () => {
    setLd(true);
    const data = await apiFetch('/api/team/all');
    if (data) setList(data);
    setLd(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd  = useCallback(() => { setForm(EMPTY_MEMBER); setImgMode('url'); setModal('add'); }, []);
  const openEdit = (m) => { setForm({ ...m, active: !!m.active }); setImgMode('url'); setModal(m); };

  useEffect(() => { if (props.addTrigger > 0) openAdd(); }, [props.addTrigger, openAdd]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUpl(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, photo_url: res.data.url }));
      toast('Photo uploaded');
    } catch {
      toast('Upload failed');
    } finally {
      setUpl(false);
    }
  };

  const save = async () => {
    if (modal === 'add') {
      await apiFetch('/api/team', { method: 'POST', body: JSON.stringify(form) });
    } else {
      await apiFetch(`/api/team/${modal.id}`, { method: 'PUT', body: JSON.stringify(form) });
    }
    setModal(null);
    toast('Saved');
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this team member?')) return;
    await apiFetch(`/api/team/${id}`, { method: 'DELETE' });
    toast('Deleted');
    load();
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <>
      <div className="dash-table-wrap">
        {loading ? <div className="dash-loading">Loading…</div> : (
          <table className="dash-table">
            <thead>
              <tr><th>Photo</th><th>Name</th><th>Role</th><th>Order</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {list.map(m => (
                <tr key={m.id}>
                  <td>
                    {m.photo_url
                      ? <img className="td-thumb" src={m.photo_url} alt={m.name} style={{ borderRadius: '50%' }} />
                      : <div style={{ width: 44, height: 44, background: 'var(--black-3)', borderRadius: '50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem' }}>👤</div>
                    }
                  </td>
                  <td className="td-title">{m.name}</td>
                  <td style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>{m.role}</td>
                  <td>{m.sort_order}</td>
                  <td><span className={`td-badge ${m.active ? 'active' : 'inactive'}`}>{m.active ? 'Active' : 'Hidden'}</span></td>
                  <td>
                    <div className="td-actions">
                      <button className="btn-icon" onClick={() => openEdit(m)}>Edit</button>
                      <button className="btn-icon danger" onClick={() => del(m.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && list.length === 0 && <div className="dash-empty">No team members yet.</div>}
      </div>

      {modal && (
        <Modal title={modal === 'add' ? 'Add Team Member' : 'Edit Member'} onClose={() => setModal(null)}>
          <div className="modal-body">
            <div className="modal-row">
              <Field label="Full Name">
                <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Said A." />
              </Field>
              <Field label="Sort Order">
                <input type="number" value={form.sort_order} onChange={e => set('sort_order', +e.target.value)} />
              </Field>
            </div>
            <Field label="Role / Title">
              <input value={form.role} onChange={e => set('role', e.target.value)} placeholder="Lead Photographer" />
            </Field>
            <div className="modal-field">
              <label>Photo</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem' }}>
                <button type="button" className={`filter-btn${imgMode === 'url' ? ' active' : ''}`} onClick={() => setImgMode('url')}>Paste URL</button>
                <button type="button" className={`filter-btn${imgMode === 'upload' ? ' active' : ''}`} onClick={() => setImgMode('upload')}>Upload File</button>
              </div>
              {imgMode === 'url' ? (
                <input value={form.photo_url} onChange={e => set('photo_url', e.target.value)} placeholder="https://…" />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} style={{ color: 'var(--muted)', fontSize: '0.8rem', flex: 1 }} />
                  {uploading && <span style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>Uploading…</span>}
                </div>
              )}
            </div>
            {form.photo_url && (
              <img className="modal-preview" src={form.photo_url} alt="preview" style={{ height: 100, width: 100, borderRadius: '50%', objectFit: 'cover' }} />
            )}
            <div className="modal-check-row">
              <input type="checkbox" id="team-active" checked={!!form.active} onChange={e => set('active', e.target.checked)} />
              <label htmlFor="team-active">Active (visible on website)</label>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn-save" onClick={save}>Save</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════
// TAB: BRANDS
// ══════════════════════════════════════════════════════
const EMPTY_BRAND = { name: '', logo_url: '', website_url: '', sort_order: 0, active: true };

function BrandsTab(props) {
  const { toast } = props;
  const [list, setList]       = useState([]);
  const [loading, setLd]      = useState(true);
  const [modal, setModal]     = useState(null);
  const [form, setForm]       = useState(EMPTY_BRAND);
  const [imgMode, setImgMode] = useState('url');
  const [uploading, setUpl]   = useState(false);
  const fileRef               = useRef();

  const load = useCallback(async () => {
    setLd(true);
    const data = await apiFetch('/api/brands/all');
    if (data) setList(data);
    setLd(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd  = useCallback(() => { setForm(EMPTY_BRAND); setImgMode('url'); setModal('add'); }, []);
  const openEdit = (b) => { setForm({ ...b, active: !!b.active }); setImgMode('url'); setModal(b); };

  useEffect(() => { if (props.addTrigger > 0) openAdd(); }, [props.addTrigger, openAdd]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUpl(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, logo_url: res.data.url }));
      toast('Logo uploaded');
    } catch {
      toast('Upload failed');
    } finally {
      setUpl(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast('Brand name is required'); return; }
    if (modal === 'add') {
      await apiFetch('/api/brands', { method: 'POST', body: JSON.stringify(form) });
    } else {
      await apiFetch(`/api/brands/${modal.id}`, { method: 'PUT', body: JSON.stringify(form) });
    }
    setModal(null);
    toast('Saved');
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this brand?')) return;
    await apiFetch(`/api/brands/${id}`, { method: 'DELETE' });
    toast('Deleted');
    load();
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <>
      <div className="dash-table-wrap">
        {loading ? <div className="dash-loading">Loading…</div> : (
          <table className="dash-table">
            <thead>
              <tr><th>Logo</th><th>Name</th><th>Website</th><th>Order</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {list.map(b => (
                <tr key={b.id}>
                  <td>
                    {b.logo_url
                      ? <img className="td-thumb" src={b.logo_url} alt={b.name} style={{ objectFit: 'contain', background: 'var(--black-3)' }} />
                      : <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>Text only</span>
                    }
                  </td>
                  <td className="td-title">{b.name}</td>
                  <td style={{ color:'var(--muted)', fontSize:'0.75rem', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {b.website_url || '—'}
                  </td>
                  <td>{b.sort_order}</td>
                  <td><span className={`td-badge ${b.active ? 'active' : 'inactive'}`}>{b.active ? 'Active' : 'Hidden'}</span></td>
                  <td>
                    <div className="td-actions">
                      <button className="btn-icon" onClick={() => openEdit(b)}>Edit</button>
                      <button className="btn-icon danger" onClick={() => del(b.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && list.length === 0 && <div className="dash-empty">No brands yet.</div>}
      </div>

      {modal && (
        <Modal title={modal === 'add' ? 'Add Brand' : 'Edit Brand'} onClose={() => setModal(null)}>
          <div className="modal-body">
            <div className="modal-row">
              <Field label="Brand Name">
                <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="MTN Ghana" />
              </Field>
              <Field label="Sort Order">
                <input type="number" value={form.sort_order} onChange={e => set('sort_order', +e.target.value)} />
              </Field>
            </div>
            <Field label="Website (optional)">
              <input value={form.website_url} onChange={e => set('website_url', e.target.value)} placeholder="https://…" />
            </Field>
            <div className="modal-field">
              <label>Logo (optional — name is shown if no logo)</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem' }}>
                <button type="button" className={`filter-btn${imgMode === 'url' ? ' active' : ''}`} onClick={() => setImgMode('url')}>Paste URL</button>
                <button type="button" className={`filter-btn${imgMode === 'upload' ? ' active' : ''}`} onClick={() => setImgMode('upload')}>Upload File</button>
              </div>
              {imgMode === 'url' ? (
                <input value={form.logo_url} onChange={e => set('logo_url', e.target.value)} placeholder="https://…" />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} style={{ color: 'var(--muted)', fontSize: '0.8rem', flex: 1 }} />
                  {uploading && <span style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>Uploading…</span>}
                </div>
              )}
            </div>
            {form.logo_url && (
              <img className="modal-preview" src={form.logo_url} alt="preview" style={{ objectFit: 'contain' }} />
            )}
            <div className="modal-check-row">
              <input type="checkbox" id="brand-active" checked={!!form.active} onChange={e => set('active', e.target.checked)} />
              <label htmlFor="brand-active">Active (visible on website)</label>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn-save" onClick={save}>Save</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════
// TAB: SERVICES
// ══════════════════════════════════════════════════════
const EMPTY_SVC = { icon: '📷', name: '', description: '', sort_order: 0, active: true };
let tmpKey = 0;
const toRows = (subs = []) => subs.map(ss => ({ key: `s${ss.id}`, id: ss.id, name: ss.name }));

// Editable list of sub-services. Existing rows keep their id, so renaming
// one keeps the photos & videos filed under it.
function SubServiceEditor({ rows, setRows }) {
  const update = (key, name) => setRows(r => r.map(x => x.key === key ? { ...x, name } : x));
  const remove = (key) => setRows(r => r.filter(x => x.key !== key));
  const move   = (i, d) => setRows(r => {
    const j = i + d; if (j < 0 || j >= r.length) return r;
    const c = [...r]; [c[i], c[j]] = [c[j], c[i]]; return c;
  });
  const add = () => setRows(r => [...r, { key: `n${++tmpKey}`, id: null, name: '' }]);

  return (
    <div className="modal-field">
      <label>Sub-services</label>
      <div className="sub-list">
        {rows.map((row, i) => (
          <div className="sub-row" key={row.key}>
            <input
              value={row.name}
              onChange={e => update(row.key, e.target.value)}
              placeholder="e.g. Wedding & Engagement"
              autoFocus={!row.id && i === rows.length - 1}
            />
            <button type="button" className="btn-icon" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">↑</button>
            <button type="button" className="btn-icon" onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label="Move down">↓</button>
            <button type="button" className="btn-icon danger" onClick={() => remove(row.key)} aria-label="Remove">✕</button>
          </div>
        ))}
        {rows.length === 0 && <div className="field-hint">No sub-services yet.</div>}
      </div>
      <button type="button" className="btn-icon" style={{ alignSelf: 'flex-start', marginTop: '0.5rem' }} onClick={add}>+ Add sub-service</button>
      <span className="field-hint">Removing a sub-service keeps its photos & videos, but files them under the service only.</span>
    </div>
  );
}

function ServicesTab(props) {
  const { toast } = props;
  const [services, setServices] = useState([]);
  const [loading, setLd]        = useState(true);
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState(EMPTY_SVC);
  const [subRows, setSubRows]   = useState([]);

  const load = useCallback(async () => {
    setLd(true);
    const data = await apiFetch('/api/content/services/all');
    if (data) setServices(data);
    setLd(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = useCallback(() => {
    setForm(EMPTY_SVC);
    setSubRows([]);
    setModal('add');
  }, []);

  useEffect(() => { if (props.addTrigger > 0) openAdd(); }, [props.addTrigger, openAdd]);
  const openEdit = (s) => {
    setForm({ ...s, active: !!s.active });
    setSubRows(toRows(s.sub_services));
    setModal(s);
  };

  const save = async () => {
    const payload = {
      ...form,
      sub_services: subRows
        .map(r => ({ id: r.id, name: r.name.trim() }))
        .filter(r => r.name),
    };
    if (modal === 'add') {
      await apiFetch('/api/content/services', { method: 'POST', body: JSON.stringify(payload) });
    } else {
      await apiFetch(`/api/content/services/${modal.id}`, { method: 'PUT', body: JSON.stringify(payload) });
    }
    setModal(null);
    toast('Saved');
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    await apiFetch(`/api/content/services/${id}`, { method: 'DELETE' });
    toast('Deleted');
    load();
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <>
      {loading ? <div className="dash-loading">Loading…</div> : (
        <div className="dash-services-grid">
          {services.map(s => (
            <div className="dash-service-card" key={s.id}>
              <div className="dsc-head">
                <div>
                  <div className="dsc-name">{s.name}</div>
                  <span className={`td-badge ${s.active ? 'active' : 'inactive'}`}>
                    {s.active ? 'Active' : 'Hidden'}
                  </span>
                </div>
                <span className="dsc-icon">{s.icon}</span>
              </div>
              <p className="dsc-desc">{s.description}</p>
              <div className="dsc-items">
                {(s.sub_services || []).map(ss => <span className="dsc-item-tag" key={ss.id}>{ss.name}</span>)}
              </div>
              <div className="dsc-actions">
                <button className="btn-icon" onClick={() => openEdit(s)}>Edit</button>
                <button className="btn-icon danger" onClick={() => del(s.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <Modal title={modal === 'add' ? 'Add Service' : 'Edit Service'} onClose={() => setModal(null)}>
          <div className="modal-body">
            <div className="modal-row">
              <Field label="Icon (emoji)">
                <input value={form.icon} onChange={e => set('icon', e.target.value)} placeholder="📷" />
              </Field>
              <Field label="Sort Order">
                <input type="number" value={form.sort_order} onChange={e => set('sort_order', +e.target.value)} />
              </Field>
            </div>
            <Field label="Service Name">
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Photography" />
            </Field>
            <Field label="Description">
              <textarea rows={3} value={form.description} onChange={e => set('description', e.target.value)} />
            </Field>
            <SubServiceEditor rows={subRows} setRows={setSubRows} />
            <div className="modal-check-row">
              <input type="checkbox" id="svc-active" checked={!!form.active} onChange={e => set('active', e.target.checked)} />
              <label htmlFor="svc-active">Active (visible on website)</label>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn-cancel" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn-save" onClick={save}>Save</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════
// TAB: SETTINGS
// ══════════════════════════════════════════════════════

const SOCIALS = [
  { key: 'social_instagram', label: 'Instagram', placeholder: 'https://instagram.com/yourpage' },
  { key: 'social_youtube',   label: 'YouTube',   placeholder: 'https://youtube.com/@yourchannel' },
  { key: 'social_tiktok',    label: 'TikTok',    placeholder: 'https://tiktok.com/@yourpage' },
  { key: 'social_linkedin',  label: 'LinkedIn',  placeholder: 'https://linkedin.com/company/yourpage' },
  { key: 'social_x',         label: 'X',         placeholder: 'https://x.com/yourhandle' },
];

// A settings image: paste a link or upload a file, with a preview
function ImageSettingField({ label, value, onChange, toast, placeholder }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.url);
      toast('Photo uploaded — click Save All Settings');
    } catch (err) {
      toast(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="settings-field">
      <label>{label} — upload or paste a link</label>
      <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
        <input ref={fileRef} type="file" accept="image/*"
          onChange={e => upload(e.target.files?.[0])}
          style={{ color: 'var(--muted)', fontSize: '0.8rem', border: 'none', padding: 0, background: 'none' }} />
        {uploading && <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Uploading…</span>}
      </div>
      {value && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.8rem', marginTop: '0.4rem' }}>
          <img src={value} alt={`${label} preview`} style={{ width: 220, maxWidth: '100%', aspectRatio: '4 / 5', objectFit: 'cover' }} />
          <button type="button" className="btn-icon danger" onClick={() => onChange('')}>Use default</button>
        </div>
      )}
    </div>
  );
}

// Slideshow slides are stored in settings as JSON:
// [{ type: 'image' | 'video' | 'youtube', url }]
const isVideoUrl = (url = '') => /\.(mp4|webm|mov)(\?|#|$)/i.test(url);
const slideTypeOf = (url = '') => getYtId(url) ? 'youtube' : isVideoUrl(url) ? 'video' : 'image';
const parseSlides = (raw) => {
  try {
    const a = JSON.parse(raw || '[]');
    if (!Array.isArray(a)) return [];
    return a
      .map(s => typeof s === 'string' ? { type: slideTypeOf(s), url: s } : s)
      .filter(s => s && s.url);
  } catch { return []; }
};

// Hero background: an image, a video (uploaded file, direct .mp4/.webm
// link, or YouTube link), or a slideshow of images. The single image doubles
// as the video's poster/fallback.
function HeroBackgroundField({ settings, setSetting, toast }) {
  const type = ['video', 'slideshow'].includes(settings.hero_bg_type) ? settings.hero_bg_type : 'image';
  const [progress, setProgress] = useState(null); // null | 0-100
  const [slideUrl, setSlideUrl] = useState('');
  const imgRef   = useRef();
  const vidRef   = useRef();
  const slideRef = useRef();

  const slides    = parseSlides(settings.hero_bg_slides);
  const setSlides = (list) => setSetting('hero_bg_slides', JSON.stringify(list));

  const uploadFile = (file, kind) => {
    const fd = new FormData();
    fd.append(kind, file);
    return axios.post(`${API}/api/upload${kind === 'video' ? '/video' : ''}`, fd, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: e => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
    }).then(res => res.data.url);
  };

  const upload = async (file, kind) => {
    if (!file) return;
    setProgress(0);
    try {
      const url = await uploadFile(file, kind);
      setSetting(kind === 'video' ? 'hero_bg_video' : 'hero_bg_image', url);
      toast(`${kind === 'video' ? 'Video' : 'Image'} uploaded — click Save All Settings`);
    } catch (err) {
      toast(err.response?.data?.message || 'Upload failed');
    } finally {
      setProgress(null);
      if (imgRef.current) imgRef.current.value = '';
      if (vidRef.current) vidRef.current.value = '';
    }
  };

  // Several images and/or videos at once, uploaded in order and appended
  const uploadSlides = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const added = [];
    try {
      for (const file of list) {
        const kind = file.type.startsWith('video/') ? 'video' : 'image';
        setProgress(0);
        added.push({ type: kind, url: await uploadFile(file, kind) });
      }
      toast(`${added.length} slide${added.length > 1 ? 's' : ''} added — click Save All Settings`);
    } catch (err) {
      toast(err.response?.data?.message || 'Upload failed');
    } finally {
      if (added.length) setSlides([...slides, ...added]);
      setProgress(null);
      if (slideRef.current) slideRef.current.value = '';
    }
  };

  const addSlideUrl = () => {
    const url = slideUrl.trim();
    if (!url) return;
    setSlides([...slides, { type: slideTypeOf(url), url }]);
    setSlideUrl('');
  };
  const removeSlide = (i) => setSlides(slides.filter((_, j) => j !== i));
  const moveSlide   = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= slides.length) return;
    const c = [...slides]; [c[i], c[j]] = [c[j], c[i]]; setSlides(c);
  };

  const ytId = getYtId(settings.hero_bg_video || '');

  return (
    <div className="settings-field">
      <label>Background</label>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.8rem' }}>
        <button type="button" className={`filter-btn${type === 'image' ? ' active' : ''}`} onClick={() => setSetting('hero_bg_type', 'image')}>Image</button>
        <button type="button" className={`filter-btn${type === 'video' ? ' active' : ''}`} onClick={() => setSetting('hero_bg_type', 'video')}>Video</button>
        <button type="button" className={`filter-btn${type === 'slideshow' ? ' active' : ''}`} onClick={() => setSetting('hero_bg_type', 'slideshow')}>Slideshow</button>
      </div>

      {type === 'slideshow' && (
        <div className="settings-field">
          <label>Slides — images and videos, upload several at once or paste links ({slides.length} slide{slides.length === 1 ? '' : 's'})</label>
          <div className="slide-grid">
            {slides.map((s, i) => (
              <div className="slide-thumb" key={`${s.url}-${i}`}>
                {s.type === 'video'
                  ? <video src={s.url} muted preload="metadata" playsInline />
                  : s.type === 'youtube'
                    ? <img src={`https://img.youtube.com/vi/${getYtId(s.url)}/mqdefault.jpg`} alt={`Slide ${i + 1}`} />
                    : <img src={s.url} alt={`Slide ${i + 1}`} />}
                <span className="slide-num">
                  {i + 1}{s.type === 'video' ? ' · ▶ Video' : s.type === 'youtube' ? ' · ▶ YouTube' : ''}
                </span>
                <div className="slide-actions">
                  <button type="button" onClick={() => moveSlide(i, -1)} disabled={i === 0} aria-label="Move left">←</button>
                  <button type="button" onClick={() => moveSlide(i, 1)} disabled={i === slides.length - 1} aria-label="Move right">→</button>
                  <button type="button" className="danger" onClick={() => removeSlide(i)} aria-label="Remove slide">✕</button>
                </div>
              </div>
            ))}
            {slides.length === 0 && <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>No slides yet — add at least 2.</span>}
          </div>
          <input ref={slideRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" multiple
            onChange={e => uploadSlides(e.target.files)}
            style={{ color: 'var(--muted)', fontSize: '0.8rem', border: 'none', padding: 0, background: 'none' }} />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              style={{ flex: 1 }}
              value={slideUrl}
              onChange={e => setSlideUrl(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSlideUrl(); } }}
              placeholder="Image link, .mp4 link or YouTube link, then press Add"
            />
            <button type="button" className="btn-icon" onClick={addSlideUrl}>Add</button>
          </div>
          <div className="settings-field" style={{ maxWidth: 260, marginTop: '0.4rem' }}>
            <label>Seconds per image slide (videos play to the end)</label>
            <input
              type="number" min={2} max={30}
              value={settings.hero_bg_slide_interval || 6}
              onChange={e => setSetting('hero_bg_slide_interval', String(Math.min(30, Math.max(2, +e.target.value || 6))))}
            />
          </div>
        </div>
      )}

      {type === 'video' && (
        <div className="settings-field">
          <label>Video — upload (mp4 / webm / mov, max 100MB) or paste a link (.mp4 or YouTube)</label>
          <input
            value={settings.hero_bg_video || ''}
            onChange={e => setSetting('hero_bg_video', e.target.value)}
            placeholder="https://… .mp4  or  https://youtube.com/watch?v=…"
          />
          <input ref={vidRef} type="file" accept="video/mp4,video/webm,video/quicktime"
            onChange={e => upload(e.target.files?.[0], 'video')}
            style={{ color: 'var(--muted)', fontSize: '0.8rem', border: 'none', padding: 0, background: 'none' }} />
          {settings.hero_bg_video && (
            ytId
              ? <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt="YouTube preview" style={{ width: 320, maxWidth: '100%', marginTop: '0.4rem' }} />
              : <video src={settings.hero_bg_video} muted loop autoPlay playsInline style={{ width: 320, maxWidth: '100%', marginTop: '0.4rem', background: '#000' }} />
          )}
        </div>
      )}

      {type !== 'slideshow' && <div className="settings-field">
        <label>{type === 'video' ? 'Fallback image (shown while the video loads)' : 'Image'} — upload or paste a link</label>
        <input
          value={settings.hero_bg_image || ''}
          onChange={e => setSetting('hero_bg_image', e.target.value)}
          placeholder="https://…  (leave empty for the default photo)"
        />
        <input ref={imgRef} type="file" accept="image/*"
          onChange={e => upload(e.target.files?.[0], 'image')}
          style={{ color: 'var(--muted)', fontSize: '0.8rem', border: 'none', padding: 0, background: 'none' }} />
        {settings.hero_bg_image && (
          <img src={settings.hero_bg_image} alt="Background preview" style={{ width: 320, maxWidth: '100%', marginTop: '0.4rem' }} />
        )}
      </div>}

      {progress !== null && (
        <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Uploading… {progress}%</span>
      )}
    </div>
  );
}

function SettingsTab({ toast }) {
  const [settings, setSettings] = useState({});
  const [stats, setStats]       = useState([]);
  const [loading, setLd]        = useState(true);
  const [saved, setSaved]       = useState(false);

  const load = useCallback(async () => {
    setLd(true);
    const [s, st] = await Promise.all([
      apiFetch('/api/content/settings'),
      apiFetch('/api/content/stats'),
    ]);
    if (s) setSettings(s);
    if (st) setStats(st);
    setLd(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setSetting = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const setStat    = (id, k, v) => setStats(st => st.map(s => s.id === id ? { ...s, [k]: v } : s));

  const saveSettings = async () => {
    await apiFetch('/api/content/settings', { method: 'PUT', body: JSON.stringify(settings) });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    toast('Settings saved');
  };

  const saveStat = async (stat) => {
    await apiFetch(`/api/content/stats/${stat.id}`, { method: 'PUT', body: JSON.stringify(stat) });
    toast('Stat saved');
  };

  if (loading) return <div className="dash-loading">Loading…</div>;

  return (
    <>
      {/* Stats */}
      <div className="settings-section">
        <div className="settings-section-title">Stat Counters</div>
        <div className="stats-editor">
          {stats.map(s => (
            <div className="stat-edit-card" key={s.id}>
              <div className="stat-edit-num">{s.value}<sup>{s.suffix}</sup></div>
              <div className="stat-edit-fields">
                <input
                  style={{ background:'var(--black-3)', border:'1px solid rgba(255,255,255,0.08)', color:'var(--white)', padding:'0.5rem 0.7rem', fontSize:'0.8rem', fontFamily:'var(--font-sans)', outline:'none' }}
                  value={s.label} onChange={e => setStat(s.id, 'label', e.target.value)}
                  placeholder="Label"
                />
                <input
                  type="number"
                  style={{ background:'var(--black-3)', border:'1px solid rgba(255,255,255,0.08)', color:'var(--white)', padding:'0.5rem 0.7rem', fontSize:'0.8rem', fontFamily:'var(--font-sans)', outline:'none' }}
                  value={s.value} onChange={e => setStat(s.id, 'value', +e.target.value)}
                />
                <input
                  style={{ background:'var(--black-3)', border:'1px solid rgba(255,255,255,0.08)', color:'var(--white)', padding:'0.5rem 0.7rem', fontSize:'0.8rem', fontFamily:'var(--font-sans)', outline:'none' }}
                  value={s.suffix} onChange={e => setStat(s.id, 'suffix', e.target.value)}
                  placeholder="+"
                />
              </div>
              <button className="btn-icon" style={{ marginTop:'0.7rem' }} onClick={() => saveStat(s)}>Save</button>
            </div>
          ))}
        </div>
      </div>

      {/* Hero */}
      <div className="settings-section">
        <div className="settings-section-title">Hero Section</div>
        <div className="settings-field">
          <label>Hero Title</label>
          <input value={settings.hero_title || ''} onChange={e => setSetting('hero_title', e.target.value)} />
        </div>
        <div className="settings-field">
          <label>Hero Subtitle</label>
          <textarea rows={3} value={settings.hero_subtitle || ''} onChange={e => setSetting('hero_subtitle', e.target.value)} />
        </div>
        <HeroBackgroundField settings={settings} setSetting={setSetting} toast={toast} />
      </div>

      {/* About */}
      <div className="settings-section">
        <div className="settings-section-title">About Section</div>
        <ImageSettingField
          label="About Photo"
          value={settings.about_image}
          onChange={url => setSetting('about_image', url)}
          toast={toast}
          placeholder="https://…  (leave empty for the default photo)"
        />
        <div className="settings-field">
          <label>Paragraph 1</label>
          <textarea rows={4} value={settings.about_text_1 || ''} onChange={e => setSetting('about_text_1', e.target.value)} />
        </div>
        <div className="settings-field">
          <label>Paragraph 2</label>
          <textarea rows={4} value={settings.about_text_2 || ''} onChange={e => setSetting('about_text_2', e.target.value)} />
        </div>
        <div className="settings-row">
          <div className="settings-field">
            <label>Founder Name</label>
            <input value={settings.founder_name || ''} onChange={e => setSetting('founder_name', e.target.value)} />
          </div>
          <div className="settings-field">
            <label>Founder Title</label>
            <input value={settings.founder_title || ''} onChange={e => setSetting('founder_title', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="settings-section">
        <div className="settings-section-title">Contact Info</div>
        <div className="settings-row">
          <div className="settings-field">
            <label>Email</label>
            <input value={settings.contact_email || ''} onChange={e => setSetting('contact_email', e.target.value)} />
          </div>
          <div className="settings-field">
            <label>Phone</label>
            <input value={settings.contact_phone || ''} onChange={e => setSetting('contact_phone', e.target.value)} />
          </div>
        </div>
        <div className="settings-field">
          <label>Location</label>
          <input value={settings.contact_location || ''} onChange={e => setSetting('contact_location', e.target.value)} />
        </div>
      </div>

      {/* Social */}
      <div className="settings-section">
        <div className="settings-section-title">Social Links</div>
        <div className="settings-row">
          {SOCIALS.map(s => (
            <div className="settings-field" key={s.key}>
              <label>{s.label}</label>
              <input
                value={settings[s.key] || ''}
                onChange={e => setSetting(s.key, e.target.value.trim())}
                placeholder={s.placeholder}
              />
            </div>
          ))}
        </div>
        <span style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>
          Leave a field empty to hide that link on the website.
        </span>
      </div>

      <div className="settings-save-bar">
        <button className="btn-save" onClick={saveSettings}>Save All Settings</button>
        {saved && <span className="settings-saved">✓ Saved</span>}
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════
// DASHBOARD SHELL
// ══════════════════════════════════════════════════════
const NAV = [
  { id: 'overview',      icon: '◈', label: 'Overview' },
  { id: 'portfolio',     icon: '◻', label: 'Portfolio' },
  { id: 'videos',        icon: '▶', label: 'Videos' },
  { id: 'team',          icon: '◉', label: 'Team' },
  { id: 'brands',        icon: '◆', label: 'Brands' },
  { id: 'services',     icon: '◎', label: 'Services' },
  { id: 'settings',      icon: '◐', label: 'Settings' },
];

const PAGE_META = {
  overview:     { title: 'OVERVIEW',      sub: 'Dashboard summary' },
  portfolio:    { title: 'PORTFOLIO',     sub: 'Manage gallery items' },
  videos:       { title: 'VIDEOS',        sub: 'YouTube reels & uploaded thumbnails' },
  team:         { title: 'TEAM',          sub: 'Manage team members' },
  brands:       { title: 'BRANDS',        sub: 'Brands we work with' },
  services:     { title: 'SERVICES',      sub: 'Manage service offerings' },
  settings:     { title: 'SETTINGS',      sub: 'Site-wide content settings' },
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const [tab, setTab]    = useState('overview');
  const [toast, setToast] = useState('');
  const [counts, setCounts] = useState({});

  useEffect(() => {
    Promise.all([
      apiFetch('/api/portfolio'),
      apiFetch('/api/content/services/all'),
      apiFetch('/api/team/all'),
      apiFetch('/api/content/stats'),
    ]).then(([p, s, tm, st]) => {
      setCounts({
        portfolio:    p?.length  ?? 0,
        services:     s?.length  ?? 0,
        team:         tm?.length ?? 0,
        stats:        st?.length ?? 0,
      });
    });
  }, []);

  const showToast = useCallback((msg) => { setToast(msg); }, []);
  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  const { title, sub } = PAGE_META[tab];

  return (
    <div className="dash">
      {/* Sidebar */}
      <aside className="dash-sidebar">
        <div className="dash-logo">
          <div className="dash-logo-text">
            <img className="dash-logo-mark" src="/brand/mark-white.png" alt="" />
            <img className="dash-logo-word" src="/brand/wordmark-white.png" alt="Boom Creations" />
          </div>
          <div className="dash-logo-sub">Admin Dashboard</div>
        </div>
        <nav className="dash-nav">
          <div className="dash-nav-section">Navigation</div>
          {NAV.map(n => (
            <button
              key={n.id}
              className={`dash-nav-btn${tab === n.id ? ' active' : ''}`}
              onClick={() => setTab(n.id)}
            >
              <span className="dash-nav-icon">{n.icon}</span>
              {n.label}
            </button>
          ))}
          <div className="dash-nav-section" style={{ marginTop: '1rem' }}>Site</div>
          <Link to="/" style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.75rem 1.5rem', color:'var(--muted)', fontSize:'0.82rem', transition:'color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.color='var(--white)'}
            onMouseLeave={e => e.currentTarget.style.color='var(--muted)'}
          >
            <span className="dash-nav-icon">↗</span> View Website
          </Link>
        </nav>
        <div className="dash-footer">
          <div className="dash-user">
            <div className="dash-avatar">
              {user?.username?.[0]?.toUpperCase() || 'A'}
            </div>
            <div>
              <div className="dash-username">{user?.username || 'Admin'}</div>
              <div className="dash-role">Administrator</div>
            </div>
          </div>
          <button className="dash-logout" onClick={handleLogout}>Sign Out</button>
        </div>
      </aside>

      {/* Main */}
      <main className="dash-main">
        <div className="dash-topbar">
          <div>
            <div className="dash-page-title">{title}</div>
            <div className="dash-page-sub">{sub}</div>
          </div>
          <div className="dash-topbar-actions">
            {tab === 'portfolio'    && <button className="btn-add" onClick={() => document.dispatchEvent(new CustomEvent('dash:add'))}>+ Add Item</button>}
            {tab === 'videos'       && <button className="btn-add" onClick={() => document.dispatchEvent(new CustomEvent('dash:add'))}>+ Add Video</button>}
            {tab === 'team'         && <button className="btn-add" onClick={() => document.dispatchEvent(new CustomEvent('dash:add'))}>+ Add Member</button>}
            {tab === 'brands'       && <button className="btn-add" onClick={() => document.dispatchEvent(new CustomEvent('dash:add'))}>+ Add Brand</button>}
            {tab === 'services'     && <button className="btn-add" onClick={() => document.dispatchEvent(new CustomEvent('dash:add'))}>+ Add Service</button>}
          </div>
        </div>

        <div className="dash-content">
          {tab === 'overview'      && <OverviewTab counts={counts} />}
          {tab === 'portfolio'     && <PortfolioTabWithEvent toast={showToast} />}
          {tab === 'videos'        && <VideosTabWithEvent toast={showToast} />}
          {tab === 'team'          && <TeamTabWithEvent toast={showToast} />}
          {tab === 'brands'        && <BrandsTabWithEvent toast={showToast} />}
          {tab === 'services'      && <ServicesTabWithEvent toast={showToast} />}
          {tab === 'settings'      && <SettingsTab toast={showToast} />}
        </div>
      </main>

      {toast && <Toast msg={toast} onDone={() => setToast('')} />}
    </div>
  );
}

// Wrappers that listen to the topbar "add" button via CustomEvent
function PortfolioTabWithEvent(props) {
  const [trigger, setTrigger] = useState(0);
  useEffect(() => {
    const h = () => setTrigger(t => t + 1);
    document.addEventListener('dash:add', h);
    return () => document.removeEventListener('dash:add', h);
  }, []);
  return <PortfolioTab {...props} addTrigger={trigger} />;
}
function ServicesTabWithEvent(props) {
  const [trigger, setTrigger] = useState(0);
  useEffect(() => {
    const h = () => setTrigger(t => t + 1);
    document.addEventListener('dash:add', h);
    return () => document.removeEventListener('dash:add', h);
  }, []);
  return <ServicesTab {...props} addTrigger={trigger} />;
}
function VideosTabWithEvent(props) {
  const [trigger, setTrigger] = useState(0);
  useEffect(() => {
    const h = () => setTrigger(t => t + 1);
    document.addEventListener('dash:add', h);
    return () => document.removeEventListener('dash:add', h);
  }, []);
  return <VideosTab {...props} addTrigger={trigger} />;
}
function TeamTabWithEvent(props) {
  const [trigger, setTrigger] = useState(0);
  useEffect(() => {
    const h = () => setTrigger(t => t + 1);
    document.addEventListener('dash:add', h);
    return () => document.removeEventListener('dash:add', h);
  }, []);
  return <TeamTab {...props} addTrigger={trigger} />;
}
function BrandsTabWithEvent(props) {
  const [trigger, setTrigger] = useState(0);
  useEffect(() => {
    const h = () => setTrigger(t => t + 1);
    document.addEventListener('dash:add', h);
    return () => document.removeEventListener('dash:add', h);
  }, []);
  return <BrandsTab {...props} addTrigger={trigger} />;
}
